import React from "react";
import {
  BuildingResponse,
  OrganizationResponse,
  PinFieldDataType,
  WebAssetResponse,
  WebFieldsResponse,
} from "@akitabox/api-client";
import {
  GridColDef,
  getGridStringOperators,
  getGridSingleSelectOperators,
  getGridNumericOperators,
  GridActionsColDef,
  GridActionsCellItem,
  ValueOptions,
  getGridDateOperators,
  GridEditSingleSelectCell,
  GridEditDateCell,
  GridEditInputCell,
  GridRowModes,
  GridRowModesModel,
} from "@mui/x-data-grid-pro";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { getTypeaheadSelectOperator } from "../components/SearchableSingleSelectFilter";
import { getDatePickerOperator } from "../components/DateRangeHeaderFilter";
import { DetailSettings } from "./types";
import { EditCustomDate, isValidDate } from "../components/EditCustomDate";
import { HasAttachmentsFilter } from "./AttachmentsPreview";
import { Badge, MenuItem, Tooltip, Typography } from "@mui/material";
import {
  AccountTree,
  Announcement,
  Build,
  Cancel,
  CurrencyExchange,
  Delete,
  Edit,
  Event,
  Grading,
  Launch,
  LocationOff,
  PhotoLibrary,
  Place,
  RemoveCircle,
  Save,
} from "@mui/icons-material";
import { EditTextarea } from "../components/EditTextarea";
import { DateRangeHeaderArrayFilter } from "./DateRangeHeaderArrayFilter";

interface GetBaseColumnsParams {
  orgLevelsOptions: { label: string; value: string }[];
  buildingOptions: { label: string; value: string }[];
  pinTypeOptions: { label: string; value: string }[];
  conditionOptions: { label: string; value: string }[];
  detailSettings: DetailSettings;
  organization: OrganizationResponse;
}

interface GetDetailColumnsParams {
  detailSettings: DetailSettings;
  attachmentFilterState: boolean | null;
  setAttachmentFilterState: (state: boolean | null) => void;
  apiRef: any;
  flags: any;
  organization: OrganizationResponse;
  addConditionRating: (asset: WebAssetResponse) => void;
  addLifeCycleCost: (asset: WebAssetResponse) => void;
  handleAttachmentsPreviewOpen: (
    event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>,
    asset: WebAssetResponse
  ) => void;
  handleLocationPreviewOpen: (
    event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>,
    asset: WebAssetResponse
  ) => void;
}

interface GetCustomColumnsParams {
  uniquePinTypeFields: string[];
  pinFields: WebFieldsResponse[];
  buildingOptions: { label: string; value: string }[];
}

interface GetActionColumnsParams {
  addMaintenanceSchedule: (
    organization: OrganizationResponse,
    building: BuildingResponse,
    assets: WebAssetResponse[]
  ) => void;
  addServiceRequest: (
    organization: OrganizationResponse,
    building: BuildingResponse,
    assets: WebAssetResponse[]
  ) => void;
  addWorkOrder: (
    organization: OrganizationResponse,
    building: BuildingResponse,
    assets: WebAssetResponse[]
  ) => void;
  assetPermissions: {
    update: boolean;
    read: boolean;
  };
  decommissionAsset: (assets: WebAssetResponse[]) => void;
  deleteAsset: (assets: WebAssetResponse[]) => void;
  handleCancelClick: (id: string) => () => void;
  handleEditClick: (id: string) => void;
  handleSaveClick: (id: string) => void;
  organization: OrganizationResponse;
  permissions: {
    canDecommissionAsset: boolean;
    canRecommissionAsset: boolean;
    canDeleteAsset: boolean;
    canAddRequest: boolean;
    canAddWorkOrder: boolean;
    canAddSchedule: boolean;
    canManageAssociations: boolean;
    canManageCostAdjustments: boolean;
    canManageLifeCycle: boolean;
    canUpdateAsset: boolean;
  };
  rowModesModel: GridRowModesModel;
}

const singleSelectIsAnyOfOperator = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "isAnyOf"
);
const singleSelectIsOperator = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is"
);
const stringFilterOperators = getGridStringOperators().filter(
  (o) => o.value === "contains" || o.value === "equals"
);
const numericFilterOperators = getGridNumericOperators().filter(
  (o) =>
    o.value !== "isEmpty" && o.value !== "isNotEmpty" && o.value !== "isAnyOf"
);
const dateSelectOperator = getGridDateOperators().filter(
  (operator) => operator.value === "before" || operator.value === "after"
);

// Build the shared columns used by every asset
export const getBaseColumns = ({
  orgLevelsOptions,
  buildingOptions,
  pinTypeOptions,
  conditionOptions,
  detailSettings,
  organization,
}: GetBaseColumnsParams): GridColDef<WebAssetResponse>[] => {
  const base: GridColDef<WebAssetResponse>[] = [
    {
      field: "name",
      type: "string",
      headerName: "Name",
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) => {
        const asset = row;
        return (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                className="abx-pin-type-icon-wrap"
                style={{
                  backgroundColor: `${asset.color}`,
                }}
              >
                <img
                  src={`/img/pin_icons/${asset.icon}.svg`}
                  alt={asset.display_name}
                  style={{
                    width: 20,
                    height: 20,
                    color: `${asset.color}`,
                  }}
                />
              </div>
              <a href={`/assets/${asset._id}/overview`}>{asset.display_name}</a>
            </div>
          </>
        );
      },
      valueGetter: (value: any, row: any) => {
        return row?.display_name;
      },
      editable: true,
      sortable: true,
      filterable: true,
      minWidth: 200,
    },
    {
      field: "building.name",
      type: "singleSelect",
      headerName: "Building",
      valueOptions: buildingOptions,
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) =>
        row.building.name,
      valueFormatter: (value: any) => {
        return value?.name;
      },
      sortable: true,
      filterable: true,
      filterOperators: [
        ...singleSelectIsAnyOfOperator,
        ...getTypeaheadSelectOperator(buildingOptions).filter(
          (operator) => operator.value !== "not"
        ),
      ],
      editable: false,
      minWidth: 150,
    },
    {
      field: "pinType.name",
      type: "singleSelect",
      headerName: "Category",
      filterOperators: [
        ...singleSelectIsAnyOfOperator,
        ...getTypeaheadSelectOperator(pinTypeOptions),
      ],
      valueOptions: pinTypeOptions,
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) =>
        row.pinType.name,
      valueFormatter: (value: any) => {
        return value?.name;
      },
      sortable: true,
      filterable: true,
      editable: false,
      minWidth: 150,
    },
    {
      field: "level.name",
      type: "singleSelect",
      headerName: "Floor",
      valueOptions: orgLevelsOptions,
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) =>
        row.level?.name,
      valueFormatter: (value: any) => {
        return value?.name;
      },
      editable: false,
      filterable: true,
      filterOperators: getTypeaheadSelectOperator(orgLevelsOptions).filter(
        (operator) => operator.value !== "not"
      ),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "room.number",
      type: "string",
      headerName: "Room Number",
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) =>
        row.room?.number,
      valueFormatter: (value: any) => {
        return value?.number;
      },
      editable: false,
      filterable: true,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "room.name",
      type: "string",
      headerName: "Room Name",
      renderCell: ({ row }: GridRenderCellParams<WebAssetResponse>) =>
        row.room?.name,
      valueFormatter: (value: any) => {
        return value?.name;
      },
      editable: false,
      filterable: true,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "costlines.total_cost",
      type: "number",
      headerName: "Total Cost",
      renderCell: ({ row: asset }: GridRenderCellParams<WebAssetResponse>) => {
        if (asset.costlines && asset.costlines.length > 0) {
          const costLine = asset.costlines[0];
          return typeof costLine.total_cost === "number"
            ? `$${costLine.total_cost.toFixed(2)}`
            : "";
        } else {
          return "";
        }
      },
      editable: false,
      filterable: false,
      sortable: false,
      minWidth: 150,
    },
  ];

  if (detailSettings.showTotalCostWithAdjustments.state) {
    base.push({
      field: "costlines.total_cost_with_adjustments",
      type: "number",
      headerName: "Total Cost (Adjusted)",
      renderCell: ({ row: asset }) => {
        if (asset.costlines && asset.costlines.length > 0) {
          const costLine = asset.costlines[0];
          return typeof costLine.total_cost_with_adjustments === "number"
            ? `$${costLine.total_cost_with_adjustments.toFixed(2)}`
            : "";
        } else {
          return "";
        }
      },
      editable: false,
      filterable: true,
      filterOperators: numericFilterOperators,
      sortable: true,
      minWidth: 170,
    });
  }

  if (organization.show_condition_field) {
    base.push({
      field: "condition",
      type: "singleSelect",
      filterable: true,
      headerName: "Condition",
      renderCell: ({ row: asset }) => {
        return asset.condition?.condition_rating;
      },
      valueFormatter: (value: any) => {
        return value?.condition_rating;
      },
      valueOptions: conditionOptions,
      filterOperators: singleSelectIsOperator,
      minWidth: 150,
    });
  }

  if (organization.show_installation_date_field) {
    base.push({
      field: "installation_date",
      type: "date",
      headerName: "Installation Date",
      renderCell: ({ row: asset }) => {
        return asset.installation_date
          ? new Date(asset.installation_date).getFullYear()
          : "";
      },
      valueFormatter: (value: any) => {
        return value ? new Date(value).getFullYear() : "";
      },
      valueGetter: (value, row) => {
        if (!row.installation_date) {
          return "";
        }
        return new Date(row.installation_date);
      },
      editable: true,
      filterable: true,
      filterOperators: getDatePickerOperator(
        ["year"],
        ["is", "after", "before", "isEmpty", "isNotEmpty"]
      ),
      minWidth: 150,
      renderEditCell: (params) => (
        <EditCustomDate
          {...params}
          value={params.value}
          date_format={"YYYY"}
          type="date"
        />
      ),
    });
  }
  return base;
};

// Build the Detail/Action columns
// These columns highlight the details of the asset
// and allow the user to take action on the asset
export const getDetailColumns = ({
  detailSettings,
  attachmentFilterState,
  setAttachmentFilterState,
  apiRef,
  organization,
  flags,
  addConditionRating,
  addLifeCycleCost,
  handleAttachmentsPreviewOpen,
  handleLocationPreviewOpen,
}: GetDetailColumnsParams): GridColDef[] => {
  const numDetailSettings = Object.values(detailSettings).filter(
    (setting) => setting.state
  ).length;
  const details: GridActionsColDef[] = [
    {
      field: "Details",
      type: "actions",
      headerName: "",
      sortable: false,
      filterable: true,
      minWidth: 30,
      width: numDetailSettings * 48,
      hideable: false,
      pinnable: true,
      renderHeaderFilter: () =>
        detailSettings.showAttachments.state ? (
          <HasAttachmentsFilter
            attachmentFilterState={attachmentFilterState}
            setAttachmentFilterState={setAttachmentFilterState}
            apiRef={apiRef}
          />
        ) : null,
      getActions: ({ row: asset }: { row: any }) => {
        const onDetailsClick = () => {
          const id = asset._id; // or params.row.id depending on your data structure
          window.open(`/assets/${id}/details`, "_blank");
        };

        const actions: JSX.Element[] = [];
        if (detailSettings.showAttachments.state) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Attachments">
                  {asset.has_attachments ? (
                    <Badge
                      badgeContent={asset.count_attachments}
                      color="primary"
                    >
                      <PhotoLibrary />
                    </Badge>
                  ) : (
                    <PhotoLibrary
                      style={{
                        color: asset.has_attachments ? "" : "darkgrey",
                      }}
                    />
                  )}
                </Tooltip>
              }
              label="Photos"
              key={asset._id}
              onClick={(event) => handleAttachmentsPreviewOpen(event, asset)}
              color={asset.has_attachments ? "primary" : "inherit"}
            />
          );
        }

        if (detailSettings.showLocation.state) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Location">
                  {asset.is_placed_on_document || asset["level.name"] ? (
                    <Place />
                  ) : (
                    <LocationOff
                      style={{
                        color:
                          asset.is_placed_on_document || asset["level.name"]
                            ? ""
                            : "darkgrey",
                      }}
                    />
                  )}
                </Tooltip>
              }
              label="Location"
              key={asset._id}
              onClick={(event) => handleLocationPreviewOpen(event, asset)}
              color={
                asset.is_placed_on_document || asset["level.name"]
                  ? "primary"
                  : "inherit"
              }
            />
          );
        }

        if (detailSettings.showAssociations?.state) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Associations">
                  {asset.has_associations ? (
                    <AccountTree />
                  ) : (
                    <AccountTree
                      style={{
                        color: asset.has_associations ? "" : "darkgrey",
                      }}
                    />
                  )}
                </Tooltip>
              }
              label="Associations"
              key={asset._id}
              onClick={() => onDetailsClick()}
              color={asset.has_associations ? "primary" : "inherit"}
            />
          );
        }

        if (
          organization.show_condition_field &&
          detailSettings.showConditions?.state
        ) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Condition">
                  <Grading
                    style={{
                      color: asset.condition?.condition_rating
                        ? ""
                        : "darkgrey",
                    }}
                  />
                </Tooltip>
              }
              label="Condition"
              key={asset._id}
              onClick={() => addConditionRating(asset)}
              color={asset.condition?.condition_rating ? "primary" : "inherit"}
            />
          );
        }

        if (
          organization.show_fca &&
          flags["asset_life_cycle"] &&
          detailSettings.showTotalCostWithAdjustments?.state
        ) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Lifecycle Cost">
                  <CurrencyExchange
                    style={{
                      color: asset.costlines?.length > 0 ? "" : "darkgrey",
                    }}
                  />
                </Tooltip>
              }
              label="Lifecycle Cost"
              key={asset._id}
              onClick={() => addLifeCycleCost(asset)}
              color={asset.costlines?.length ? "primary" : "inherit"}
            />
          );
        }
        return actions;
      },
    },
  ];
  return details;
};

// Build the columns in the data grid
//  This utilizes the custom values in the assets to define the columns
//  This also uses the orgLevels to define the levels for the levels column
//  This also uses the buildings to define the buildings for the building column
//  This also uses the pinTypes to define the pinTypes for the pinType column
export const getCustomColumns = ({
  uniquePinTypeFields,
  pinFields,
  buildingOptions,
}: GetCustomColumnsParams): GridColDef[] => {
  function getFieldDefinition(field: string, row: any) {
    const fieldDefinition = {
      data_type: "text",
      acceptable_enum_values: [],
      is_editable: false,
      is_required: false,
    };
    if (row.pinType.fields) {
      const fd = row.pinType.fields.find(
        (fieldDef: any) => fieldDef?.name === field?.replace("values.", "")
      );
      if (fd) {
        fieldDefinition.data_type = fd.data_type;
        fieldDefinition.acceptable_enum_values = fd.acceptable_enum_values;
        fieldDefinition.is_editable = fd.is_editable;
        fieldDefinition.is_required = fd.is_required;
      }
      return fieldDefinition;
    }
    return fieldDefinition;
  }

  function getColumnValueOptions(columnName: string) {
    return pinFields
      .filter((pinField) => pinField.name === columnName)
      .reduce((acc: { label: string; value: string }[], pinField) => {
        pinField.acceptable_enum_values?.forEach((value) => {
          if (!acc.find((option) => option.value === value)) {
            acc.push({
              label: value,
              value,
            });
          }
        });
        return acc;
      }, [])
      .sort((a, b) => a?.label?.localeCompare(b?.label));
  }

  const customColumns: GridColDef[] = [
    ...uniquePinTypeFields
      // filter out any values where the _pinField has the property is_hidden set to true
      .filter((key) => {
        // Looking at allPinTypeFields by field.name matching the key -- if ALL of them are marked as hidde, then return false
        // const isHidden = allPinTypeFields
        //   .filter((field) => field.name === key)
        //   .every((field) => field.is_hidden);
        // if (isHidden) {
        //   return false;
        // }
        // // Find the first example of this field name in the pinTypes
        // const field = allPinTypeFields.find((field) => field.name === key);
        // if (
        //   !!field?.is_level_field ||
        //   !!field?.is_room_field ||
        //   field?.data_type === "document_array" ||
        //   field?.data_type === "tag_filter"
        // ) {
        //   return false;
        // }
        const field = pinFields.find((field) => field.name === key);
        if (field?.data_type === "document_array") {
          return false;
        }
        if (field?.data_type === "tag_filter") {
          return false;
        }
        return true;
      })
      .map((key) => {
        // const field = allPinTypeFields.find((field) => field.name === key);
        const field = pinFields.find((field) => field.name === key);

        let valueOptions: ValueOptions = [];

        let type: GridColDef["type"] = "string";
        if (field?.data_type === "float" || field?.data_type === "int") {
          type = "number";
        } else if (
          field?.data_type === "enum" ||
          field?.data_type === "boolean"
        ) {
          type = "singleSelect" as const; // Explicitly set as "singleSelect"
          valueOptions = getColumnValueOptions(field?.name);
        } else if (field?.data_type === "date") {
          type = "date";
        }

        let filterOperators;
        if (field?.name === "Type") {
          type = "singleSelect";
          filterOperators = [
            ...singleSelectIsAnyOfOperator,
            ...getTypeaheadSelectOperator(buildingOptions),
          ];
          valueOptions = getColumnValueOptions(field?.name);
          // valueOptions = pinTypeFieldTypeOptions;
        } else {
          if (field?.data_type === "float" || field?.data_type === "int") {
            filterOperators = numericFilterOperators;
          } else if (field?.data_type === "enum") {
            filterOperators = singleSelectIsOperator;
          } else if (field?.data_type === "date") {
            filterOperators = getGridDateOperators();
          } else {
            filterOperators = stringFilterOperators;
          }
        }

        return {
          field: `values.${key}`,
          renderCell: (params: GridRenderCellParams<any>) => {
            const fieldDefinition = getFieldDefinition(
              params.field,
              params.row
            );
            const { data_type } = fieldDefinition
              ? (fieldDefinition as {
                  data_type: PinFieldDataType;
                })
              : {
                  data_type: "text",
                };
            if (data_type === "url") {
              return (
                <a
                  href={params.row.values[key].value}
                  target="_blank"
                  rel="noreferrer"
                >
                  {params.row.values[key].value}
                </a>
              );
            }
            return params.formattedValue;
          },
          valueFormatter: (value: any, row: any) => {
            if (
              row.values[key] === undefined ||
              value === null ||
              value === undefined ||
              value === ""
            ) {
              return "";
            }
            switch (field?.data_type) {
              case "boolean":
                if (value === true || value === "true") return "Yes";
                if (value === false || value === "false") return "No";
                return value;
              case "date":
                return new Date(value).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                });
              case "int":
                return Math.trunc(value);
              default:
                return value;
            }
          },
          valueGetter: (value: any, row: any) => {
            if (row.values[key] === undefined) return "";
            if (field?.data_type === "date" && row.values[key].value) {
              return new Date(row.values[key].value);
            }
            return row.values[key].value;
          },
          valueSetter: (value: any, row: any) => {
            const newRow = {
              ...row,
              values: { ...row.values, [key]: { ...row.values[key], value } },
            };
            return newRow;
          },
          headerName: key,
          minWidth: 150,
          type: type as
            | "string"
            | "number"
            | "singleSelect"
            | "date"
            | "boolean",
          editable: true,
          preProcessEditCellProps: (params: any) => {
            const newValue = params.props.value;
            if (field?.data_type === "date" && newValue) {
              return {
                ...params.props,
                value: new Date(newValue).toISOString(),
              };
            }
            if (field?.data_type === "enum") {
              const fieldDefinition = getFieldDefinition(
                params.field,
                params.row
              );
              const { acceptable_enum_values } = fieldDefinition
                ? (fieldDefinition as {
                    acceptable_enum_values: string[];
                  })
                : {
                    acceptable_enum_values: [],
                  };
              return { ...params.props, options: acceptable_enum_values };
            }
            return params.props;
          },
          renderEditCell: (params: any) => {
            const fieldDefinition = getFieldDefinition(
              params.field,
              params.row
            );
            const {
              data_type,
              acceptable_enum_values,
              is_editable,
              is_required,
            } = fieldDefinition
              ? (fieldDefinition as {
                  data_type: PinFieldDataType;
                  acceptable_enum_values: string[];
                  is_editable: boolean;
                  is_required: boolean;
                })
              : {
                  data_type: "text",
                  acceptable_enum_values: [],
                  is_editable: false,
                  is_required: false,
                };
            // Need to fetch valueOptions on demand using getBuildingPintypeValueOptions

            // GridEditSingleSelectCell is really nice, but it sucks at handling the options property
            // You have to override the colDef valueOptions to sneak in the correct version.
            const valueOptions = [];
            if (!is_required) {
              valueOptions.push("");
            }
            if (data_type === "boolean") {
              valueOptions.push("true", "false");
            } else {
              valueOptions.push(...acceptable_enum_values);
            }
            const newColDef = {
              ...(params.colDef as GridColDef),
              valueOptions,
              getOptionLabel: (value: any) => {
                if (value === "") {
                  return (
                    <Typography
                      variant="body2"
                      color="grey"
                      sx={{ fontStyle: "italic" }}
                    >
                      Blank
                    </Typography>
                  );
                } else if (value === "true") {
                  return "Yes";
                } else if (value === "false") {
                  return "No";
                }
                return value;
              },
            };
            switch (data_type) {
              case "enum":
                return (
                  <GridEditSingleSelectCell
                    {...params}
                    value={params.value}
                    disabled={!is_editable}
                    colDef={newColDef}
                  >
                    {newColDef.valueOptions?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </GridEditSingleSelectCell>
                );
              case "boolean":
                return (
                  <GridEditSingleSelectCell
                    {...params}
                    value={params.value.toString()}
                    disabled={!is_editable}
                    colDef={newColDef}
                  >
                    <MenuItem value={"true"}>Yes</MenuItem>
                    <MenuItem value={"false"}>No</MenuItem>
                  </GridEditSingleSelectCell>
                );
              case "date": {
                const value = isValidDate(params.value)
                  ? new Date(params.value)
                  : "";
                return (
                  <GridEditDateCell
                    {...params}
                    variant="outlined"
                    value={value}
                    type="date"
                  />
                );
              }
              case "int":
              case "float":
                return (
                  <GridEditInputCell
                    {...params.props}
                    value={params.value}
                    type="number"
                    disabled={!is_editable}
                    variant="outlined"
                    multiline={false}
                    fullWidth
                    {...params}
                    inputProps={
                      data_type === "int" ? { step: 1 } : { step: 0.1 }
                    }
                    sx={{
                      ...(!is_editable && {
                        backgroundColor: "#f0f0f0", // Light gray background to indicate disabled state
                        color: "#a0a0a0", // Dim text color for disabled input
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0", // Lighter border for disabled state
                        },
                      }),
                    }}
                  />
                );
              case "paragraph":
              case "string":
              case "text":
              default:
                if (data_type === "paragraph" && params.hasFocus) {
                  return (
                    <EditTextarea
                      {...params}
                      value={params.value}
                      type="string"
                      disabled={!is_editable}
                    />
                  );
                }
                return (
                  <GridEditInputCell
                    {...params.props}
                    value={params.value}
                    type="string"
                    disabled={!is_editable}
                    variant="outlined"
                    multiline={data_type === "paragraph"}
                    fullWidth
                    {...params}
                    sx={{
                      ...(!is_editable && {
                        backgroundColor: "#f0f0f0", // Light gray background to indicate disabled state
                        color: "#a0a0a0", // Dim text color for disabled input
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0", // Lighter border for disabled state
                        },
                      }),
                    }}
                  />
                );
            }
          },
          filterable: true,
          filterOperators: filterOperators,
          ...(type === "singleSelect" ? { valueOptions } : {}),
        };
      }),
  ];
  return customColumns;
};

// Build the created columns
//  This is used to show the created date of the asset
export const getCreatedColumns = (): GridColDef[] => {
  const createdColumns: GridColDef[] = [
    {
      field: "created_date",
      headerName: "Created",
      renderCell: ({ row }: { row: any }) => {
        return new Date(row.created_date).toLocaleDateString();
      },
      type: "date",
      filterable: true,
      filterOperators: getGridDateOperators(),
      minWidth: 200,
      valueGetter: (row: { created_date: string | number | Date }) => {
        if (!row.created_date) {
          return "";
        }
        return new Date(row.created_date);
      },
    },
  ];
  return createdColumns;
};

// Build the modified columns
// This is used to show the modified date of the asset
export const getModifiedColumns = (): GridColDef[] => {
  const createdColumns: GridColDef[] = [
    {
      field: "modified_date",
      headerName: "Modified",
      renderCell: ({ row }: { row: any }) => {
        return new Date(row?.modified_date).toLocaleDateString();
      },
      type: "date",
      filterable: true,
      filterOperators: [
        ...dateSelectOperator,
        {
          label: "Between",
          headerLabel: "Between", // Add this for header filter translation
          value: "between",
          getApplyFilterFn: (filterItem) => {
            if (
              !filterItem.value ||
              !Array.isArray(filterItem.value) ||
              filterItem.value.length !== 2
            ) {
              return null;
            }
            const [start, end] = filterItem.value;
            return (params) => {
              if (!params.value) return false;
              const date = new Date(params.value).setHours(0, 0, 0, 0);
              const startDate = new Date(start).setHours(0, 0, 0, 0);
              const endDate = new Date(end).setHours(0, 0, 0, 0);
              return date >= startDate && date <= endDate;
            };
          },
          InputComponent: DateRangeHeaderArrayFilter,
        },
      ],
      minWidth: 200,
      valueGetter: (row: { modified_date: string | number | Date }) => {
        if (!row?.modified_date) {
          return "";
        }
        return new Date(row?.modified_date);
      },
    },
  ];
  return createdColumns;
};

// Build the action columns
//  This is used to show the action buttons for the asset
//  This includes the edit, delete, and overflow buttons
export const getActionColumns = ({
  addMaintenanceSchedule,
  addServiceRequest,
  addWorkOrder,
  assetPermissions,
  decommissionAsset,
  deleteAsset,
  handleCancelClick,
  handleEditClick,
  handleSaveClick,
  organization,
  permissions,
  rowModesModel,
}: GetActionColumnsParams): GridColDef[] => {
  const action: GridActionsColDef[] = [
    {
      field: "Actions",
      type: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      minWidth: 80,
      hideable: false,
      pinnable: false,
      getActions: ({ row: asset }: { row: any }) => {
        const isInEditMode =
          rowModesModel[asset._id]?.mode === GridRowModes.Edit;
        const hasOtherRowInEditMode = Object.keys(rowModesModel)
          .filter((key) => key !== asset._id)
          .some((key) => rowModesModel[key]?.mode === GridRowModes.Edit);

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={
                <Tooltip title="Save">
                  <Save
                    data-testid={`save-icon-${asset._id}`}
                    color="primary"
                  />
                </Tooltip>
              }
              label="Save"
              key={asset._id}
              onClick={() => handleSaveClick(asset._id)}
            />,
            <GridActionsCellItem
              icon={
                <Tooltip title="Cancel">
                  <Cancel
                    data-testid={`cancel-icon-${asset.id}`}
                    color="primary"
                  />
                </Tooltip>
              }
              key={asset._id}
              label="Cancel"
              onClick={handleCancelClick(asset._id)}
              color="inherit"
            />,
          ];
        }
        const actions = [];
        if (assetPermissions?.update) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Edit">
                  <Edit
                    data-testid={`edit-icon-${asset._id}`}
                    color={hasOtherRowInEditMode ? "disabled" : "primary"}
                  />
                </Tooltip>
              }
              label="Edit"
              disabled={hasOtherRowInEditMode}
              key={asset._id}
              onClick={() => handleEditClick(asset._id)}
              color="inherit"
            />
          );
        }
        actions.push(
          <GridActionsCellItem
            icon={<Launch />}
            label="View Asset Details"
            key={asset._id}
            showInMenu
            onClick={() => {
              const id = asset._id; // or params.row.id depending on your data structure
              window.open(`/assets/${id}/details`, "_blank");
            }}
          />
        );
        if (permissions.canAddRequest) {
          actions.push(
            <GridActionsCellItem
              icon={<Announcement />}
              label="Add Service Request"
              key={asset._id}
              showInMenu
              onClick={() => {
                addServiceRequest(organization, asset.building, [asset]);
              }}
            />
          );
        }
        if (permissions.canAddWorkOrder) {
          actions.push(
            <GridActionsCellItem
              icon={<Build />}
              label="Add Work Order"
              key={asset._id}
              showInMenu
              onClick={() => {
                addWorkOrder(organization, asset.building, [asset]);
              }}
            />
          );
        }
        if (permissions.canAddSchedule) {
          actions.push(
            <GridActionsCellItem
              icon={<Event />}
              label="Add Maintenance Schedule"
              key={asset._id}
              showInMenu
              onClick={() => {
                addMaintenanceSchedule(organization, asset.building, [asset]);
              }}
            />
          );
        }
        if (permissions.canDecommissionAsset) {
          actions.push(
            <GridActionsCellItem
              icon={<RemoveCircle sx={{ color: "red" }} />}
              label="Decommission Asset"
              key={asset._id}
              showInMenu
              onClick={() => {
                decommissionAsset([asset]);
              }}
              sx={{ color: "red" }}
            />
          );
        }
        if (permissions.canDeleteAsset) {
          actions.push(
            <GridActionsCellItem
              icon={<Delete sx={{ color: "red" }} />}
              label="Delete Asset"
              key={asset._id}
              showInMenu
              onClick={() => {
                deleteAsset([asset]);
              }}
              sx={{ color: "red" }}
            />
          );
        }
        return actions;
      },
    },
  ];
  return action;
};
